import {
  canonicalJson, generators, HOST_VALIDATION_MAX_AGE_MS, MACRO_NATIVE_VALIDATION_TIMEOUT_MS,
  macroPackageRunCommandSchema, macroPackageRunReplySchema,
  type CompiledPackageRef, type MacroLiveDetection, type MacroPackageRunCommand, type MacroPackageRunReply,
  type MacroRuntimeMode, type MacroPackageCaptureTarget, type MacroContentViewport, type PipelineHostTarget, type PlaycastMessage, type PlaycastMessageTarget, type VisualTestPreviewSettings,
} from '@playcastdotio/Messaging';

/**
 * Signed Playcast envelope for actions the frozen generator unions do not cover (`process/macroPackageRun`,
 * host-electron macro-package IPC). Same header/signature scheme as `formatMessage`.
 */
export function playcastEnvelope(target: string, action: string, isReply: boolean, message: unknown): PlaycastMessage<PlaycastMessageTarget> {
  const base = generators.process.macroRun({ command: 'capabilities', requestId: 'envelope', runId: '' });
  const header = { ...base.header, target, action, isReply };
  return { ...base, signature: btoa(JSON.stringify(header)), header, body: { ...base.body, message } } as unknown as PlaycastMessage<PlaycastMessageTarget>;
}

export interface MacroPackageStart {
  runId: string; mode: MacroRuntimeMode; package: CompiledPackageRef; packageSha256: string;
  liveDetection?: MacroLiveDetection; leaseWaitMs?: number; previewSettings?: VisualTestPreviewSettings;
  captureTarget?: MacroPackageCaptureTarget;
  contentViewport?: MacroContentViewport;
}
type ReplyListener = (reply: MacroPackageRunReply) => void;
interface Pending { command: MacroPackageRunCommand; resolve(reply: MacroPackageRunReply): void; timer: ReturnType<typeof setTimeout> }

/**
 * A terminal reply must never be dropped for carrying a session status field or an error string newer than this
 * build's enum; `macroSessionState` maps unknown codes to `native_runtime_error`.
 */
const lenientReplySchema = macroPackageRunReplySchema.innerType().omit({ errorCode: true }).passthrough();

/** Admission is immediate (`running`/`loading`); package load and the run report later on the same requestId. */
export const PACKAGE_ADMISSION_TIMEOUT_MS = 10_000;
const CANCEL_TIMEOUT_MS = 45_000;
const STATUS_TIMEOUT_MS = 5_000;

/**
 * Correlated `processMacroPackageRun` commands. Each request resolves with the first reply carrying its
 * `requestId` and `runId` (start admission, cancel acknowledgement, the `'*'` status summary). Every other
 * reply — node telemetry, terminal outcomes, per-session status rows — goes to `onReply` listeners.
 */
export class NativeMacroPackageBridge {
  private readonly pending = new Map<string, Pending>();
  private readonly replyListeners = new Set<ReplyListener>();
  private readonly disconnectListeners = new Set<() => void>();

  public constructor(private readonly send: (message: PlaycastMessage<PlaycastMessageTarget>) => void,
    private readonly now: () => number = Date.now) {}

  public capabilities(): Promise<MacroPackageRunReply> {
    return this.request({ command: 'capabilities', requestId: crypto.randomUUID(), runId: '' }, STATUS_TIMEOUT_MS);
  }
  /** Trusted server adapters preserve native correlation and explicit runtime mode verbatim. */
  public requestOwned(command: MacroPackageRunCommand): Promise<MacroPackageRunReply> {
    const parsed = macroPackageRunCommandSchema.safeParse(command);
    if (!parsed.success || this.pending.has(command.requestId)) return Promise.resolve(this.failure(command, 'invalid_command'));
    const timeout = parsed.data.command === 'validate' ? MACRO_NATIVE_VALIDATION_TIMEOUT_MS : parsed.data.command === 'cancel' ? CANCEL_TIMEOUT_MS
      : parsed.data.command === 'start' ? PACKAGE_ADMISSION_TIMEOUT_MS : STATUS_TIMEOUT_MS;
    return this.request(parsed.data, timeout);
  }
  public start(start: MacroPackageStart): Promise<MacroPackageRunReply> {
    return this.request({ command: 'start', requestId: crypto.randomUUID(), runId: start.runId, mode: start.mode,
      package: start.package, packageSha256: start.packageSha256,
      ...(start.captureTarget ? { captureTarget: start.captureTarget } : {}),
      ...(start.liveDetection ? { liveDetection: start.liveDetection } : {}),
      ...(start.previewSettings ? { previewSettings: start.previewSettings } : {}),
      ...(start.contentViewport ? { contentViewport: start.contentViewport } : {}),
      ...(start.leaseWaitMs !== undefined ? { leaseWaitMs: start.leaseWaitMs } : {}) }, PACKAGE_ADMISSION_TIMEOUT_MS);
  }
  /** Verifies package bytes, kernel, references, watch capacity and touch mapping without sending input. */
  public async validate(input: { runId: string; package: CompiledPackageRef; packageSha256: string;
    expectedHost?: PipelineHostTarget; validationContextDigest?: string; liveDetection?: MacroLiveDetection;
    captureTarget?: MacroPackageCaptureTarget }): Promise<MacroPackageRunReply> {
    const capabilities = input.expectedHost && input.validationContextDigest ? undefined : await this.capabilities();
    const expectedHost = input.expectedHost ?? capabilities?.host;
    const validationContextDigest = input.validationContextDigest ?? capabilities?.validationContextDigest;
    if (!expectedHost || !validationContextDigest) return { requestId: crypto.randomUUID(), runId: input.runId, revision: '',
      outcome: 'failed', inputsSent: false, errorCode: 'host_validation_stale' };
    const command = { command: 'validate' as const, requestId: crypto.randomUUID(), ...input,
      expectedHost, validationContextDigest };
    return this.request(command, MACRO_NATIVE_VALIDATION_TIMEOUT_MS);
  }
  /** `runId: '*'` cancels every session on this Playjector. */
  public cancel(runId: string): Promise<MacroPackageRunReply> {
    return this.request({ command: 'cancel', requestId: crypto.randomUUID(), runId }, CANCEL_TIMEOUT_MS);
  }
  /** `runId: '*'` resolves with the summary; one row per active session reaches `onReply`. */
  public status(runId: string): Promise<MacroPackageRunReply> {
    return this.request({ command: 'status', requestId: crypto.randomUUID(), runId }, STATUS_TIMEOUT_MS);
  }
  /** Diagnostics only; native playback rejects score controls on input-producing runs. */
  public setPreviewSensitivity(runId: string, sensitivity: number, rawScores: boolean): Promise<MacroPackageRunReply> {
    return this.request({ command: 'preview-sensitivity', requestId: crypto.randomUUID(), runId, sensitivity, rawScores }, STATUS_TIMEOUT_MS);
  }

  public onReply(listener: ReplyListener): () => void {
    this.replyListeners.add(listener);
    return () => this.replyListeners.delete(listener);
  }
  public onDisconnected(listener: () => void): () => void {
    this.disconnectListeners.add(listener);
    return () => this.disconnectListeners.delete(listener);
  }

  /** Feeds a Playjector → host IPC message; true when it was a package-run reply. */
  public receive(message: PlaycastMessage<PlaycastMessageTarget>): boolean {
    const { target, action, isReply } = message.header;
    if (target !== 'process' || (action as string) !== 'macroPackageRun' || !isReply) return false;
    this.notify(message.body.message);
    return true;
  }

  public notify(input: unknown): void {
    const parsed = lenientReplySchema.safeParse(input);
    if (!parsed.success) {
      console.error('[MacroPackage] rejected native reply', parsed.error.flatten(), input);
      return;
    }
    let reply = parsed.data as MacroPackageRunReply;
    const candidate = this.pending.get(reply.requestId);
    const pending = candidate?.command.runId === reply.runId ? candidate : undefined;
    if ((reply.outcome === 'validated' || reply.outcome === 'capabilities') && !macroPackageRunReplySchema.safeParse(input).success) {
      if (pending) this.finish(this.failure(pending.command, 'host_validation_stale'));
      return;
    }
    if (pending?.command.command === 'validate' && reply.outcome === 'validated') {
      const receipt = reply.validationReceipt;
      if (!receipt || receipt.packageSha256 !== pending.command.packageSha256
        || receipt.validationContextDigest !== pending.command.validationContextDigest) {
        return this.finish(this.failure(pending.command, 'host_validation_stale'));
      }
      if (canonicalJson(receipt.host) !== canonicalJson(pending.command.expectedHost)) {
        return this.finish(this.failure(pending.command, 'host_validation_target_changed'));
      }
      const lifetime = receipt.expiresAtUnixMs - receipt.verifiedAtUnixMs;
      if (lifetime <= 0 || lifetime > HOST_VALIDATION_MAX_AGE_MS) {
        return this.finish(this.failure(pending.command, 'host_validation_stale'));
      }
      // Correlation to this pending command proves freshness. Native wall-clock time is metadata and can move
      // independently of Chromium's clock, so retain only its bounded lifetime and issue Host-local timestamps.
      const verifiedAtUnixMs = this.now();
      reply = { ...reply, validationReceipt: { ...receipt, verifiedAtUnixMs,
        expiresAtUnixMs: verifiedAtUnixMs + lifetime } };
    }
    if (pending && pending.command.runId === reply.runId) return this.finish(reply);
    for (const listener of this.replyListeners) listener(reply);
  }

  /** The Playjector IPC dropped: every in-flight request fails and listeners mark their sessions. */
  public handleDisconnect(): void {
    for (const pending of [...this.pending.values()]) this.finish(this.failure(pending.command, 'native_ipc_disconnected'));
    for (const listener of this.disconnectListeners) listener();
  }

  private request(command: MacroPackageRunCommand, timeoutMs: number): Promise<MacroPackageRunReply> {
    if (!macroPackageRunCommandSchema.safeParse(command).success) return Promise.resolve(this.failure(command, 'invalid_command'));
    return new Promise((resolve) => {
      const timer = setTimeout(() => this.finish(this.failure(command, this.timeoutCode(command))), timeoutMs);
      this.pending.set(command.requestId, { command, resolve, timer });
      try { this.send(playcastEnvelope('process', 'macroPackageRun', false, command)); }
      catch { this.finish(this.failure(command, 'native_send_failed')); }
    });
  }

  private timeoutCode(command: MacroPackageRunCommand): string {
    if (command.command === 'capabilities') return 'native_capabilities_timeout';
    if (command.command === 'validate') return 'native_validation_timeout';
    return 'native_reply_timeout';
  }

  private finish(reply: MacroPackageRunReply): void {
    const pending = this.pending.get(reply.requestId);
    if (!pending) return;
    clearTimeout(pending.timer);
    this.pending.delete(reply.requestId);
    pending.resolve(reply);
  }

  /** Host-side failure in reply shape; verification transport codes never claim a native result. */
  private failure(command: MacroPackageRunCommand, errorCode: string): MacroPackageRunReply {
    return { requestId: command.requestId, runId: command.runId, revision: '', outcome: 'failed', inputsSent: false, errorCode } as MacroPackageRunReply;
  }
}
